"use client";

/**
 * @contract
 *   Назначение: Компонент сворачиваемого текста (первые N строк + "показать ещё")
 *   Автор:      AI Agent (Фаза A')
 *   Дата:       2026-03-17
 */

import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

interface ExpandableTextProps {
  text: string;
  maxLines?: number;
  className?: string;
}

export function ExpandableText({ text, maxLines = 3, className = "" }: ExpandableTextProps) {
  const [expanded, setExpanded] = useState(false);
  const [needsExpand, setNeedsExpand] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textRef.current) {
      const lineHeight = parseFloat(getComputedStyle(textRef.current).lineHeight) || 20;
      const maxHeight = lineHeight * maxLines;
      setNeedsExpand(textRef.current.scrollHeight > maxHeight + 4);
    }
  }, [text, maxLines]);

  return (
    <div className={className}>
      <div
        ref={textRef}
        className="text-white/70 leading-relaxed font-sans text-sm whitespace-pre-wrap transition-all duration-300"
        style={{
          maxHeight: expanded ? "none" : `${maxLines * 1.5}em`,
          overflow: "hidden",
        }}
      >
        {text}
      </div>
      {needsExpand && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-2 flex items-center gap-1 text-[11px] text-white/40 hover:text-white/70 transition-colors uppercase tracking-wider font-bold"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3 h-3" />
              Свернуть
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              Показать полностью
            </>
          )}
        </button>
      )}
    </div>
  );
}
